import { Component, TemplateRef } from '@angular/core';
import { ToolBaseComponent } from '../tool-base';
import { ApiService } from 'src/app/shared/services/api.service';
import { EngineeringToolsApiService } from 'src/app/shared/services/engineering-tools-api';
import { UnitConverterService } from 'src/app/shared/services/unit-converter.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { MeasurementContext, StarterDisconnectTypeDescriptions, StarterEnclosureDescriptions, StarterManufacturerDescriptions, StarterTypeDescriptions } from 'src/app/shared/data/engineering-enums';

interface StarterDetailField {
  key: string;
  label: string;
  context?: MeasurementContext;
  dropdown?: string;
  boolean?: boolean;
  unit?: string;
}

@Component({
  selector: 'app-starter-selector',
  templateUrl: './starter-selector.html',
  styleUrl: './starter-selector.scss',
  standalone: false
})
export class StarterSelectorComponent extends ToolBaseComponent {
  expandedStarter: any = null;
  selectedStarter: any = null;
  filterViewModel: any = {};
  private filterDialogRef: MatDialogRef<unknown> | null = null;
  private searchDelayed: ReturnType<typeof setTimeout> | undefined;

  private starterDescriptions: Record<string, ReadonlyMap<number, string>> = {
    DisconnectType: StarterDisconnectTypeDescriptions,
    Enclosure: StarterEnclosureDescriptions,
    Manufacturer: StarterManufacturerDescriptions,
    StarterType: StarterTypeDescriptions,
  };

  filterInputs = [
    { key: 'Search', label: 'Search', type: 'text' },
    { key: 'PowerConsumption', label: 'Power Consumption', type: 'power' },
    { key: 'MotorFla', label: 'Motor FLA', type: 'number' },
    { key: 'StarterType', label: 'Starter Type', type: 'dropdown' },
    { key: 'Manufacturer', label: 'Manufacturer', type: 'dropdown' },
    { key: 'Voltage', label: 'Voltage', type: 'dropdown' },
    { key: 'Enclosure', label: 'Enclosure', type: 'dropdown' },
    { key: 'CompressorType', label: 'Compressor Type', type: 'dropdown' },
  ];

  starterDetailFields: StarterDetailField[] = [
    { key: 'Power', label: 'Power', context: MeasurementContext.Power },
    { key: 'Voltage', label: 'Voltage', unit: 'V' },
    { key: 'Phase', label: 'Phase' },
    { key: 'Frequency', label: 'Frequency', unit: 'Hz' },
    { key: 'Manufacturer', label: 'Manufacturer', dropdown: 'Manufacturer' },
    { key: 'StarterType', label: 'Starter Type', dropdown: 'StarterType' },
    { key: 'Enclosure', label: 'Enclosure', dropdown: 'Enclosure' },
    { key: 'DisconnectType', label: 'Disconnect Type', dropdown: 'DisconnectType' },
    { key: 'InterruptRating', label: 'Interrupt Rating' },
    { key: 'TripAmps', label: 'Trip Amps' },
    { key: 'CPT', label: 'CPT' },
    { key: 'WireRange', label: 'Wire Range (Line)' },
    { key: 'WireRangeLoad', label: 'Wire Range (Load)' },
    { key: 'Height', label: 'Height', context: MeasurementContext.InchOrMm },
    { key: 'Width', label: 'Width', context: MeasurementContext.InchOrMm },
    { key: 'Depth', label: 'Depth', context: MeasurementContext.InchOrMm },
    { key: 'Weight', label: 'Weight', context: MeasurementContext.Weight },
    { key: 'IsScrew', label: 'Screw Compressor', boolean: true },
    { key: 'IsPiston', label: 'Piston Compressor', boolean: true },
    { key: 'Amps', label: 'Maximum Amps', unit: 'A' },
    { key: 'Model', label: 'Model' },
  ];

  get activeFilters() {
    return this.filterInputs.filter(filter => {
      const value = this.viewModel[filter.key]?.Value;
      const selection = filter.type === 'power' ? value?.Value : value;
      if (selection == null || String(selection).trim() === '' || Number(selection) === -1) return false;
      if (filter.key === 'CompressorType'
        && this.dropdownLabel(filter.key, selection).trim().toLowerCase() === 'all') return false;
      if ((filter.type === 'number' || filter.type === 'power') && Number(selection) === 0) return false;
      return true;
    }).map(filter => ({
      ...filter,
      text: filter.type === 'power'
        ? this.formatMeasurementToDisplay(this.viewModel[filter.key].Value, MeasurementContext.Power, 0)
          + ' ' + this.getUnitText(MeasurementContext.Power)
        : filter.type === 'dropdown'
        ? this.dropdownLabel(filter.key, this.viewModel[filter.key].Value)
        : String(this.viewModel[filter.key].Value),
    }));
  }

  trackFilter(index: number, filter: { key: string }): string {
    return filter.key;
  }

  openFilters(template: TemplateRef<unknown>) {
    this.filterViewModel = structuredClone(this.viewModel);
    this.filterDialogRef = this.dialog.open(template, {
      width: '770px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: true,
    });
  }

  cancelFilterChanges() {
    this.filterDialogRef?.close();
  }

  applyFilterChanges() {
    this.viewModel = this.filterViewModel;
    this.filterDialogRef?.close();
    this.onChange(null);
  }

  dropdownLabel(key: string, value: any): string {
    const description = this.starterDescriptions[key]?.get(value);
    if (description !== undefined) return description;
    const options = this.viewModel[key]?.AvailableList?.Value;
    return options?.find((option: { Key: any; Value: string }) => option.Key === value)?.Value ?? String(value ?? '');
  }

  clearFilter(key: string, update: boolean = true) {
    const filter = this.filterInputs.find(input => input.key === key);
    if (filter?.type === 'power') {
      this.viewModel[key].Value = { ...this.viewModel[key].Value, Value: 0 };
    } else if (filter?.type === 'dropdown') {
      const allOption = this.viewModel[key].AvailableList?.Value?.find(
        (option: { Key: any; Value: string }) => option.Value.trim().toLowerCase() === 'all');
      this.viewModel[key].Value = allOption?.Key
        ?? (key === 'CompressorType' || key === 'OilPumpStarter' ? 'All' : -1);
    } else {
      this.viewModel[key].Value = filter?.type === 'number' ? 0 : filter?.type === 'multiplier' ? 1 : '';
    }
    if (update) this.onChange(null);
  }

  resetAllFilters() {
    this.filterInputs.forEach(filter => this.clearFilter(filter.key, false));
    this.viewModel.StarterMultiplier.Value = 1;
    this.onChange(null);
  }

  toggleStarterDetails(starter: any) {
    this.expandedStarter = this.expandedStarter === starter ? null : starter;
  }

  starterDetailValue(starter: any, field: StarterDetailField): string {
    const value = starter[field.key];
    if (value == null) return '';
    if (field.context !== undefined) {
      const formatted = this.formatUnits(value, field.context, 2);
      return formatted ? formatted + ' ' + this.getUnitText(field.context) : '';
    }
    if (field.dropdown) return this.dropdownLabel(field.dropdown, value);
    if (field.boolean) return value ? 'Yes' : 'No';
    return String(value) + (field.unit ? ' ' + field.unit : '');
  }

  constructor(
    protected override hrtApi: ApiService,
    protected override api: EngineeringToolsApiService,
    protected override unitConverter: UnitConverterService,
    public dialog: MatDialog,
    private readonly route: ActivatedRoute,
  ) {
    super(hrtApi, api, unitConverter);
    this.componentType = "StarterSelector";
  }

  ngOnInit() {
    this.initializeFromRouteIdOrCreate(this.route);
  }

  searchTextChanged() {
    clearTimeout(this.searchDelayed);
    this.searchDelayed = setTimeout(() => this.onChange(null), 500);
  }

  ngOnDestroy() {
    clearTimeout(this.searchDelayed);
  }

  onChange(event: any) {
    clearTimeout(this.searchDelayed);
    this.expandedStarter = null;
    this.selectedStarter = null;
    this.updateComponent();
  }
}
